<template>
  <v-card class="pa-4 mb-4" outlined>
    <v-row>
      <v-col cols="12">
        <h3 class="subtitle-1 mb-3">🕐 Planification de la publication</h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="6">
        <v-menu
          ref="dateMenu"
          v-model="page.dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="schedule.startDate"
              label="Date de début"
              prepend-icon="mdi-calendar"
              readonly
              outlined
              v-bind="attrs"
              v-on="on"
              hide-details
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="schedule.startDate"
            no-title
            scrollable
            @input="page.dateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="6">
        <v-menu
          ref="timeMenu"
          v-model="page.timeMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="schedule.startTime"
              label="Heure de début"
              prepend-icon="mdi-clock"
              readonly
              outlined
              v-bind="attrs"
              v-on="on"
              hide-details
            ></v-text-field>
          </template>
          <v-time-picker
            v-model="schedule.startTime"
            format="24hr"
            @input="page.timeMenu = false"
          ></v-time-picker>
        </v-menu>
      </v-col>

      <v-col cols="6">
        <v-text-field
          v-model.number="schedule.articlesPerDay"
          label="Articles par jour"
          type="number"
          min="1"
          outlined
          hide-details
          prepend-icon="mdi-file-document-multiple"
        ></v-text-field>
      </v-col>

      <v-col cols="6">
        <v-text-field
          v-model.number="schedule.intervalMinutes"
          label="Publier en moyenne toutes les X minutes"
          type="number"
          min="1"
          outlined
          hide-details
          prepend-icon="mdi-timer"
        ></v-text-field>
      </v-col>

      <v-col cols="12" class="text-center">
        <v-btn
          color="primary"
          outlined
          :loading="page.loading"
          :disabled="
            page.loading ||
            !schedule.startDate ||
            !schedule.startTime ||
            urls.length === 0 ||
            !templateId
          "
          @click="generateSchedule"
          prepend-icon="mdi-calendar-multiple"
        >
          Planification
        </v-btn>
      </v-col>
    </v-row>

    <v-expand-transition>
      <div v-if="false && page.showCalendar && scheduledItems.length > 0">
        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col cols="12" class="d-flex justify-space-between align-center">
            <h4 class="subtitle-2 mb-0">📋 Calendrier de publication</h4>
            <dot-menu
              position="page-top-right"
              :excelButton="excelButton"
              @exportExcel="exportToXlsx"
            />
          </v-col>
        </v-row>

        <v-data-table
          :headers="tableHeaders"
          :items="scheduledItems"
          :items-per-page="10"
          :page="page.currentPage"
          @update:page="page.currentPage = $event"
          @update:items-per-page="page.itemsPerPage = $event"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50],
          }"
          dense
        >
          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              :color="item.status === 'success' ? 'success' : 'error'"
              dark
            >
              {{ item.status === "success" ? "OK" : "Erreur" }}
            </v-chip>
          </template>
          <template v-slot:item.title="{ item }">
            <span
              v-if="item.title"
              class="text-truncate"
              style="max-width: 250px; display: inline-block"
              :title="item.title"
            >
              {{ item.title }}
            </span>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.date="{ item }">
            <span v-if="item.date">{{ formatDate(item.date) }}</span>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.time="{ item }">
            <span v-if="item.time">{{ item.time }}</span>
            <span v-else class="grey--text">-</span>
          </template>
          <template v-slot:item.url="{ item }">
            <v-chip
              small
              outlined
              :title="item.url"
              class="text-truncate"
              style="max-width: 300px"
            >
              {{ getDisplayUrl(item.url) }}
            </v-chip>
          </template>
        </v-data-table>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import { computed, reactive, ref, watch } from "@vue/composition-api";
import * as XLSX from "xlsx";
import DotMenu from "@/components/common/menus/DotMenu.vue";
import useProxy from "@/hooks/useProxy";
import useAxios from "@/hooks/useAxios";

export default {
  name: "ScheduleFilter",
  components: {
    DotMenu,
  },
  props: {
    initialValue: {
      type: Object,
      default: () => ({
        startDate: null,
        startTime: "09:00",
        articlesPerDay: 5,
        intervalMinutes: 60,
      }),
    },
    urls: {
      type: Array,
      default: () => [],
    },
    siteId: {
      type: Number,
      default: null,
    },
    templateId: {
      type: Number,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();
    const { axiosPost, axiosGet } = useAxios();

    const page = reactive({
      dateMenu: false,
      timeMenu: false,
      showCalendar: false,
      loading: false,
      currentPage: 1,
      itemsPerPage: 10,
    });

    const schedule = reactive({
      startDate: props.initialValue.startDate,
      startTime: props.initialValue.startTime,
      articlesPerDay: props.initialValue.articlesPerDay,
      intervalMinutes: props.initialValue.intervalMinutes,
    });

    const scheduledItems = reactive([]);

    const scheduleParams = computed(() => {
      if (!schedule.startDate) return null;

      return {
        start_date: schedule.startDate,
        start_time: schedule.startTime,
        articles_per_day: schedule.articlesPerDay,
        interval_minutes: schedule.intervalMinutes,
      };
    });

    watch(
      scheduleParams,
      (newValue) => {
        emit("scheduleChanged", newValue);
      },
      { deep: true }
    );

    watch(
      scheduledItems,
      (newValue) => {
        if (newValue.length > 0) {
          const validItems = newValue.filter((item) => item.date && item.time);
          const scheduleDatetime = validItems.map((item) => {
            return `${item.date}T${item.time}:00`;
          });

          const fullSchedule = {
            ...scheduleParams.value,
            items: scheduleDatetime,
            itemsWithTitles: validItems.map((item) => ({
              scheduled: `${item.date}T${item.time}:00`,
              url: item.url,
              title: item.title,
            })),
          };
          emit("scheduleChanged", fullSchedule);
        } else {
          emit("scheduleChanged", scheduleParams.value);
        }
      },
      { deep: true }
    );

    const tableHeaders = [
      { text: "Statut", value: "status", width: "80px" },
      { text: "Titre", value: "title" },
      { text: "Date", value: "date" },
      { text: "Heure", value: "time", width: "80px" },
      { text: "URL", value: "url" },
    ];

    const formatLocalDateTime = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      const localDateTimeString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      const localDate = new Date(localDateTimeString);
      return localDate.toISOString();
    };

    const generateSchedule = async () => {
      if (
        !schedule.startDate ||
        !schedule.startTime ||
        props.urls.length === 0
      ) {
        page.showCalendar = false;
        return;
      }

      page.loading = true;
      scheduledItems.length = 0;

      try {
        const sources = props.urls.map((url) => ({ url }));

        const scheduledList = [];
        const [hours, minutes] = schedule.startTime.split(":").map(Number);
        
        const startParts = schedule.startDate.split("-");
        const year = parseInt(startParts[0]);
        const month = parseInt(startParts[1]) - 1;
        const day = parseInt(startParts[2]);
        
        let currentDate = new Date(Date.UTC(year, month, day, hours, minutes, 0));
        let dayIndex = 0;
        let articleOfDay = 0;

        for (let i = 0; i < props.urls.length; i++) {
          if (articleOfDay > 0) {
            const randomDeviation = Math.round(Math.random() * 10) - 5;
            const randomInterval = schedule.intervalMinutes + randomDeviation;
            currentDate.setUTCMinutes(currentDate.getUTCMinutes() + randomInterval);
          } else {
            const initialDeviation = Math.round(Math.random() * 10) - 5;
            currentDate.setUTCMinutes(currentDate.getUTCMinutes() + initialDeviation);
          }
          
          scheduledList.push(formatLocalDateTime(currentDate));
          
          articleOfDay++;
          if (articleOfDay >= schedule.articlesPerDay) {
            dayIndex++;
            articleOfDay = 0;
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);
            currentDate.setUTCHours(hours, minutes, 0, 0);
          }
        }

        const createResponse = await axiosPost(
          "/news-radar-services/planning_batch",
          {
            site_id: props.siteId,
            aif_custom_prompt_template_id: props.templateId,
            sources,
            scheduled_list: scheduledList,
          }
        );

        const batchId = createResponse.data.id;

        emit("batchCreated", { batchId });

        page.showCalendar = true;
      } catch (error) {
        console.error("Erreur lors de la planification:", error);
        for (const url of props.urls) {
          scheduledItems.push({
            url,
            title: null,
            status: "failed",
            date: null,
            time: null,
            error: error.message,
          });
        }
        page.showCalendar = true;
      } finally {
        page.loading = false;
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    const getDisplayUrl = (url) => {
      try {
        const urlObj = new URL(url);
        return urlObj.hostname + urlObj.pathname;
      } catch {
        return url.length > 40 ? url.substring(0, 40) + "..." : url;
      }
    };

    const exportToXlsx = () => {
      const data = scheduledItems.map((item) => ({
        Statut: item.status === "success" ? "OK" : "Erreur",
        Titre: item.title || "-",
        Date: item.date || "-",
        Heure: item.time || "-",
        URL: item.url,
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Planning");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `planning_publication_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    };

    const excelButton = computed(() => {
      if (scheduledItems.length === 0) return null;
      return {
        excelData: new Blob([""], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename: `planning_publication_${
          new Date().toISOString().split("T")[0]
        }.xlsx`,
        disabled: false,
      };
    });

    return {
      page,
      schedule,
      scheduleParams,
      tableHeaders,
      scheduledItems,
      generateSchedule,
      formatDate,
      getDisplayUrl,
      showCalendar: page.showCalendar,
      exportToXlsx,
      excelButton,
    };
  },
};
</script>

<style scoped>
.subtitle-1 {
  color: #1976d2;
  font-weight: 600;
}
</style>
